import { createElement, forwardRef, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import { useDispatchApp, useSelectorApp } from "../../../../lib/redux";
import { Controller, useForm, useFormContext } from "react-hook-form";
import {
  changeCreativeWithOptionalTextRegeneration,
  getMatchedCreativeEntryForAdSlot,
} from "../../../../services/creativeChangeWithTextRegeneration";
import { useTranslationApp } from "../../../../lib/i18next";
import {
  LINKEDIN,
  MIN_DIMENSIONS_CONTENT,
  FORMATS_MEDIA_LINKEDIN,
  ADS_CAMPAIGN_STATUS_DRAFT,
  VIDEO_FORMATS,
} from "../../../../utils/constants";
import {
  BUTTON_CHANGE_CONTENT,
  BUTTON_SELECT_CONTENT,
  FIELD_CONTENT_IS_BEING_PROCESSED,
  FIELD_REQUIRED,
  FIELD_AD_PREVIEW_CONTENT_CHANGE_WARNING_DRAFT,
  FIELD_AD_PREVIEW_CONTENT_CHANGE_WARNING_CREATED,
} from "../../../../i18n/keysTranslations";
import Typography from "@mui/material/Typography";
import CardMedia from "../../../../components/Media/CardMedia";
import IconButtonAddContent from "../../../../components/Buttons/IconButtonAddContent";
import { getPathDatabaseByRole } from "../../../../actions/auth";
import { getReactHookFormNestedError } from "../../../../utils/errors";
import TooltipOptimization from "../../../Tooltip/TooltipOptimization";
import LinkedinContainerIntegration from "../../../../components/LinkedIn/LinkedinContainerIntegration";
import _ from "lodash";
import { buildCreativeOptimizationPrompt } from "../../../../utils/creativeOptimizationContext";
import AdPreviewRegeneratingTextsOverlay from "../../AdPreviewRegeneratingTextsOverlay";

// LinkedIn ad formats
const AD_FORMAT_VIDEO = "SINGLE_VIDEO";
const AD_FORMAT_CAROUSEL = "CAROUSEL";

const getConfigurationPreviewType = ({ mediaUrls = [], platformData }) => {
  const showAdPreview = Boolean(
    platformData?.adAccountID && platformData?.integrationID
  );

  const showMediaUpload = Boolean(!showAdPreview && mediaUrls?.length > 0);

  return {
    showAdPreview,
    showMediaUpload,
  };
};

const SelectNewContent = ({
  basicPath,
  control,
  userID,
  adID,
  currentMediaIndex,
  onTriggerGeneratePreview,
  onRegeneratingTextsChange = () => {},
  mediaUrl,
  platformData,
  websiteURL,
  language,
  errorMediaUrl,
  isDisabledFields,
  showButton = true,
  assetGroupStatus,
}) => {
  const { t } = useTranslationApp();
  const {
    setValue,
    watch,
    getValues,
    trigger,
    creativeOptimizations,
    onRemoveCreativeOptimization,
  } = useFormContext();
  const dispatch = useDispatchApp();

  const objective = watch("objective");
  const productContext = watch("productContext");
  const targetContext = watch("targetContext");
  const adFormat = watch(`platformsData.${LINKEDIN}.adFormat`);
  const isVideo = adFormat === AD_FORMAT_VIDEO;
  const platformsData = watch("platformsData") || {};

  const onChangeContent = async (content) => {
    await changeCreativeWithOptionalTextRegeneration({
      platform: LINKEDIN,
      content,
      basicPath,
      adID,
      currentMediaIndex,
      isCarousel: adFormat === AD_FORMAT_CAROUSEL,
      dispatch,
      getValues,
      setValue,
      watch,
      trigger,
      t,
      language,
      campaignContext: watch("context") || "",
      adFormat: watch(`platformsData.${LINKEDIN}.adFormat`),
      creativeOptimizations,
      onRemoveCreativeOptimization,
      onTriggerGeneratePreview: onTriggerGeneratePreview || (() => {}),
      onRegeneratingTextsChange,
    });
  };

  const creativeEntry = getMatchedCreativeEntryForAdSlot({
    creativeOptimizations,
    basicPath,
  });

  useEffect(() => {
    if (!creativeEntry?.title) return;
    const element = document.getElementById(
      `${basicPath}.mediaUrls.[${currentMediaIndex}]`
    );
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    // eslint-disable-next-line
  }, [creativeEntry?.slotKey, basicPath]);

  if (!showButton) return null;

  const showTooltip = !_.isEmpty(creativeEntry);

  return (
    <TooltipOptimization
      isOpen={showTooltip}
      titleTooltip={creativeEntry?.title}
      actionTooltip={creativeEntry?.action}
    >
      <Box
        id={`${basicPath}.mediaUrls.[${currentMediaIndex}]`}
        data-field={`${basicPath}.mediaUrls.[${currentMediaIndex}]`}
        sx={{
          width: "100%",
        }}
      >
        <Controller
          name={`${basicPath}.mediaUrls.[${currentMediaIndex}]`}
          control={control}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
            validate: (value) => {
              if (value?.includes("blob:")) {
                return t(FIELD_CONTENT_IS_BEING_PROCESSED);
              }
            },
          }}
          render={() => (
            <IconButtonAddContent
              id={`add-content-media-${basicPath}`}
              mode="button"
              disabled={isDisabledFields || assetGroupStatus !== "draft"}
              minDimensions={MIN_DIMENSIONS_CONTENT}
              showButtonUpload={true}
              pathStorage={`adsCampaigns/${userID}/`}
              modeUpload="sync"
              onAddContent={(contents) => {
                onChangeContent(contents[0]);
              }}
              initialValues={{
                language,
                platform: LINKEDIN,
                ...(creativeEntry
                  ? { prompt: buildCreativeOptimizationPrompt(creativeEntry) }
                  : {}),
              }}
              onCallbackSaveCanva={(mediaUrl) => {
                const canvaContent = {
                  url: mediaUrl,
                  type: "upload",
                };
                onChangeContent(canvaContent);
              }}
              multiple={false}
              acceptedFormats={isVideo ? VIDEO_FORMATS : FORMATS_MEDIA_LINKEDIN}
              urlContent={mediaUrl}
              textButton={
                mediaUrl ? BUTTON_CHANGE_CONTENT : BUTTON_SELECT_CONTENT
              }
              showGenerateWithAIOption={true}
              showUploadDeviceOption={true}
              showGalleryOption={true}
              propsPostsAdsPlatforms={platformsData}
              propsGenerateAI={{
                websiteURL,
                language,
                integrationID: platformData?.integrationID,
                adAccountID: platformData?.adAccountID,
                campaignContext: {
                  objective,
                  productContext,
                  targetContext,
                },
              }}
              color="secondary"
              sx={{
                gap: 1,
                alignItems: "flex-start",
                width: "100%",
                mb: 1,
              }}
            />
          )}
        />
        {errorMediaUrl && (
          <Typography color="error" variant="caption">
            {errorMediaUrl?.message}
          </Typography>
        )}
        {!errorMediaUrl && (
          <Typography
            color="text.secondary"
            sx={{ fontSize: "0.8rem", textAlign: "center" }}
          >
            {assetGroupStatus === ADS_CAMPAIGN_STATUS_DRAFT
              ? t(FIELD_AD_PREVIEW_CONTENT_CHANGE_WARNING_DRAFT)
              : t(FIELD_AD_PREVIEW_CONTENT_CHANGE_WARNING_CREATED)}
          </Typography>
        )}
      </Box>
    </TooltipOptimization>
  );
};

const AdPreviewLinkedin = forwardRef((props) => {
  const {
    adID,
    mediaUrls = [],
    mediaData = [],
    basicPath,
    basicPathAdGroup = basicPath, // Default to basicPath if not provided
  } = props;
  const [currentMediaIndex] = useState(0);
  const [isRegeneratingTexts, setIsRegeneratingTexts] = useState(false);

  const currentMediaUrl = mediaUrls[currentMediaIndex];

  const { isGeneralDisabledFields, watch, setValue, getValues } =
    useFormContext();

  const platformData = watch(`platformsData.${LINKEDIN}`) || {};
  const isIncompatibleCampaign = platformData?.isIncompatible;

  const isIncompatibleAssetsGroup =
    getValues(`${basicPathAdGroup || basicPath}.isIncompatible`) || false;

  const isIncompatibleAd = getValues(`${basicPath}.isIncompatible`) || false;
  const assetGroupStatus = watch(`${basicPathAdGroup || basicPath}.status`);

  const isDisabledFields =
    isGeneralDisabledFields ||
    isIncompatibleCampaign ||
    isIncompatibleAssetsGroup ||
    isIncompatibleAd;

  const { control, errors } = useForm({
    defaultValues: {},
  });
  const errorMediaUrl = getReactHookFormNestedError(
    errors,
    `${basicPath}.mediaUrls`
  );

  const dispatch = useDispatchApp();

  const linkedinIntegrations = useSelectorApp(
    (state) => state?.user?.integrations?.[LINKEDIN]
  );
  const noHasPlatformData = _.isEmpty(linkedinIntegrations);

  const { userID } = dispatch(getPathDatabaseByRole());

  const language = watch("language");
  const websiteURL = watch("websiteURL");
  const platforms = watch("platforms");

  const { showMediaUpload } = getConfigurationPreviewType({
    mediaData,
    mediaUrls,
    platformData,
  });

  const commonSxContainer = {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: 1.5,
    maxWidth: "100%",
    width: "100%",
    height: "100%",
  };

  if (showMediaUpload) {
    return (
      <Box sx={commonSxContainer}>
        {noHasPlatformData &&
          !isDisabledFields &&
          createElement(LinkedinContainerIntegration, {
            onChangeIntegration: ({ integrationID, adAccountID }) => {
              setValue(
                `platformsData.${LINKEDIN}.integrationID`,
                integrationID
              );
              setValue(`platformsData.${LINKEDIN}.adAccountID`, adAccountID);
            },
          })}
        <SelectNewContent
          userID={userID}
          adID={adID}
          basicPath={basicPath}
          control={control}
          mediaUrl={currentMediaUrl}
          currentMediaIndex={currentMediaIndex}
          platforms={platforms}
          platformData={platformData}
          websiteURL={websiteURL}
          language={language}
          errorMediaUrl={errorMediaUrl}
          isDisabledFields={isDisabledFields}
          showButton={true}
          assetGroupStatus={assetGroupStatus}
          onRegeneratingTextsChange={setIsRegeneratingTexts}
        />
        <CardMedia
          url={currentMediaUrl}
          sx={{
            height: "auto",
            maxWidth: "100%",
            width: "100%",
          }}
        />
        <AdPreviewRegeneratingTextsOverlay open={isRegeneratingTexts} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        maxWidth: "100%",
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
        }}
      >
        <SelectNewContent
          userID={userID}
          adID={adID}
          basicPath={basicPath}
          control={control}
          mediaUrl={currentMediaUrl}
          currentMediaIndex={currentMediaIndex}
          platforms={platforms}
          platformData={platformData}
          websiteURL={websiteURL}
          language={language}
          errorMediaUrl={errorMediaUrl}
          isDisabledFields={isDisabledFields}
          showButton={true}
          assetGroupStatus={assetGroupStatus}
          onRegeneratingTextsChange={setIsRegeneratingTexts}
        />
      </Box>
      {currentMediaUrl && (
        <CardMedia
          url={currentMediaUrl}
          sx={{
            height: "auto",
            maxWidth: "100%",
            width: "100%",
          }}
        />
      )}
      <AdPreviewRegeneratingTextsOverlay open={isRegeneratingTexts} />
    </Box>
  );
});

AdPreviewLinkedin.displayName = "AdPreviewLinkedin";

export default AdPreviewLinkedin;
